import { MenuContentDataAttrs } from "../../../core/ui/menu/data.js";
import { MenuCSSVars } from "../../../core/ui/menu/vars.js";
import { containsComposed, getBlockExtent, getDeepActiveElement, getElementChildren, getElementPadding, getInlineExtent, measureElement, measureElementChildren, observeElements, readCSSLength, restoreAttributes, snapshotAttributes, walkAncestors } from "@videojs/utils/dom";
//#region src/dom/ui/menu/popup.ts
/**
* Coordinates sibling Contents and sizes their shared Popup.
*
* @internal
*/
function createMenuPopup() {
	const contents = /* @__PURE__ */ new Set();
	const exitFrames = /* @__PURE__ */ new Map();
	let element = null;
	let frame = 0;
	function scheduleSync() {
		cancelAnimationFrame(frame);
		sync();
		frame = requestAnimationFrame(sync);
	}
	function getChildren(parent) {
		return [...contents].filter((content) => content.parent === parent);
	}
	function getActiveChild(parent) {
		return getChildren(parent).find(({ menu }) => {
			const input = menu.input.current;
			return input.active && input.status !== "ending";
		}) ?? null;
	}
	function getClosingChild(parent) {
		return getChildren(parent).find(({ menu }) => {
			const input = menu.input.current;
			return input.active && input.status === "ending";
		}) ?? null;
	}
	function cancelChildExit(content) {
		cancelAnimationFrame(exitFrames.get(content) ?? 0);
		exitFrames.delete(content);
	}
	function scheduleChildExit(content) {
		if (exitFrames.has(content)) return;
		const exitFrame = requestAnimationFrame(() => {
			exitFrames.set(content, requestAnimationFrame(() => {
				exitFrames.delete(content);
				if (!contents.has(content) || getActiveChild(content.menu)) return;
				content.element.removeAttribute(MenuContentDataAttrs.childOpen);
				sync();
			}));
		});
		exitFrames.set(content, exitFrame);
	}
	function getCurrentContent() {
		let current = [...contents].find((content) => content.parent === null) ?? null;
		while (current) {
			const child = getActiveChild(current.menu) ?? (exitFrames.has(current) ? getClosingChild(current.menu) : null);
			if (!child) return current;
			current = child;
		}
		return null;
	}
	function setInactive(content, inactive) {
		if (inactive) {
			content.element.setAttribute("aria-hidden", "true");
			content.element.setAttribute("inert", "");
		} else restoreAttributes(content.element, content.accessibility);
	}
	function restoreFocusBeforeHiding(content) {
		const hasFocus = () => {
			const active = getDeepActiveElement(content.element.ownerDocument);
			return active instanceof Element && containsComposed(content.element, active);
		};
		if (!hasFocus()) return;
		content.menu.restoreFocus();
		const parentInput = content.parent?.input.current;
		if (hasFocus() && parentInput?.active && parentInput.status !== "ending") content.menu.triggerElement?.focus();
		const active = getDeepActiveElement(content.element.ownerDocument);
		if (hasFocus() && active instanceof HTMLElement) active.blur();
	}
	function getAvailableWidth(popup) {
		return walkAncestors(popup, (ancestor) => {
			const width = readCSSLength(ancestor, MenuCSSVars.availableWidth);
			return width !== null && width > 0 ? width : void 0;
		}) ?? null;
	}
	function getVerticalScrollbarWidth(content) {
		if (content.scrollHeight <= content.clientHeight) return 0;
		const style = getComputedStyle(content);
		const inlineBorder = (Number.parseFloat(style.borderInlineStartWidth) || 0) + (Number.parseFloat(style.borderInlineEndWidth) || 0);
		return Math.max(0, content.offsetWidth - content.clientWidth - inlineBorder);
	}
	function measureContent(content, availableWidth) {
		const children = getElementChildren(content, (child) => child instanceof HTMLElement && !child.hidden);
		if (children.length === 0) return measureElement(content, {
			overflow: "both",
			styles: {
				width: "max-content",
				height: "auto",
				minWidth: "0px",
				maxWidth: "none"
			}
		});
		return measureElementChildren(content, {
			children,
			includePadding: true,
			maxWidth: availableWidth,
			measure: (child, width) => measureElement(child, {
				overflow: "both",
				styles: {
					insetInlineStart: "0px",
					insetInlineEnd: "auto",
					width: width === void 0 ? "max-content" : `${width}px`,
					height: "auto",
					minWidth: "0px",
					maxWidth: "none"
				}
			})
		});
	}
	function sync() {
		if (!element) return;
		const inactiveContents = /* @__PURE__ */ new Map();
		for (const content of contents) {
			const activeChild = getActiveChild(content.menu);
			if (activeChild) {
				cancelChildExit(content);
				content.menu.highlight(null);
				content.element.setAttribute(MenuContentDataAttrs.childOpen, "");
			} else if (getClosingChild(content.menu) && content.element.hasAttribute(MenuContentDataAttrs.childOpen)) scheduleChildExit(content);
			else {
				cancelChildExit(content);
				content.element.removeAttribute(MenuContentDataAttrs.childOpen);
			}
			const input = content.menu.input.current;
			const isExitingPage = content.parent !== null && input.active && input.status === "ending";
			inactiveContents.set(content, activeChild !== null || isExitingPage);
			setInactive(content, false);
		}
		for (const [content, inactive] of inactiveContents) {
			const input = content.menu.input.current;
			if (content.parent !== null && input.active && input.status === "ending") restoreFocusBeforeHiding(content);
			setInactive(content, inactive);
		}
		const current = getCurrentContent();
		if (!current) return;
		const popupPadding = current.parent === null ? getElementPadding(element) : null;
		const inlinePadding = popupPadding ? getInlineExtent(popupPadding) : 0;
		const blockPadding = popupPadding ? getBlockExtent(popupPadding) : 0;
		const availableWidth = getAvailableWidth(element);
		const contentAvailableWidth = availableWidth === null ? null : Math.max(0, availableWidth - inlinePadding);
		const size = measureContent(current.element, contentAvailableWidth);
		const width = Math.ceil(size.width + inlinePadding);
		const height = Math.ceil(size.height + blockPadding);
		element.style.setProperty(MenuCSSVars.width, `${width}px`);
		element.style.setProperty(MenuCSSVars.height, `${height}px`);
		const scrollbarWidth = getVerticalScrollbarWidth(current.element);
		if (scrollbarWidth > 0) element.style.setProperty(MenuCSSVars.width, `${width + scrollbarWidth}px`);
	}
	function setElement(next) {
		element = next;
		scheduleSync();
	}
	function registerContent(registration) {
		const registered = {
			...registration,
			accessibility: snapshotAttributes(registration.element, ["aria-hidden", "inert"]),
			stopObserving: () => {},
			unsubscribe: () => {}
		};
		registered.unsubscribe = registration.menu.input.subscribe(scheduleSync);
		registered.stopObserving = observeElements({
			root: registration.element,
			getElements: () => [registration.element],
			mutations: {
				childList: true,
				subtree: true,
				characterData: true
			},
			onChange: scheduleSync
		});
		contents.add(registered);
		registration.menu.setContentElement(registration.element);
		scheduleSync();
		return () => {
			cancelChildExit(registered);
			contents.delete(registered);
			registered.unsubscribe();
			registered.stopObserving();
			restoreAttributes(registration.element, registered.accessibility);
			registration.element.removeAttribute(MenuContentDataAttrs.childOpen);
			if (registration.menu.contentElement === registration.element) registration.menu.setContentElement(null);
			scheduleSync();
		};
	}
	function destroy() {
		cancelAnimationFrame(frame);
		for (const exitFrame of exitFrames.values()) cancelAnimationFrame(exitFrame);
		exitFrames.clear();
		for (const content of contents) {
			content.unsubscribe();
			content.stopObserving();
			restoreAttributes(content.element, content.accessibility);
			content.element.removeAttribute(MenuContentDataAttrs.childOpen);
			if (content.menu.contentElement === content.element) content.menu.setContentElement(null);
		}
		contents.clear();
		element = null;
	}
	return {
		get element() {
			return element;
		},
		setElement,
		registerContent,
		sync,
		destroy
	};
}
//#endregion
export { createMenuPopup };
